import {
  Button, Card, DateInput, ErrorState, Field, LoadingState, Notice, Row, Stack, invalidDateIn, useApi, useI18n,
  type GuardianLinkResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * Bringing a withdrawn child back (Q32).
 *
 * The return opens a new stay; the first one, and everything recorded in it, stays readable. The withdrawal ended
 * every guardian's link, so the administration says who comes back with the child: each former guardian is offered
 * as they were, ticked unless they were suspended, and anyone else is added anew from the guardians card.
 */
export function ReenrollCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, today } = useI18n();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(today());
  // What the administrator changed; every other former guardian is ticked unless they were suspended.
  const [choices, setChoices] = useState<ReadonlyMap<number, boolean>>(new Map());

  const guardians = useQuery({
    queryKey: ['child', childId, 'guardians', true],
    queryFn: () => api.guardians.forChild(childId, true),
  });

  // The links the withdrawal ended, the latest one for each person.
  const former = latestPerGuardian(
    (guardians.data ?? []).filter((link) => link.endedReason === 'ChildWithdrawn'));
  const ticked = (link: GuardianLinkResponse) => choices.get(link.id) ?? link.accessLevel !== 'Suspended';
  const restoring = former.filter(ticked);

  const reenroll = useMutation({
    mutationFn: () => api.children.reenroll(childId, {
      classId: null,
      enrollmentDate: date,
      restoreGuardianLinkIds: restoring.map((link) => link.id),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['child', childId] });
      void queryClient.invalidateQueries({ queryKey: ['children'] });
    },
  });

  const toggle = (link: GuardianLinkResponse, on: boolean) =>
    setChoices((current) => new Map(current).set(link.id, on));

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget) || !date) return;
    reenroll.mutate();
  }

  return (
    <Card title={t('child.reenroll')} hint={t('child.reenroll.hint')}>
      <form onSubmit={submit} noValidate>
        <Stack>
          {reenroll.isError && <ErrorState error={reenroll.error} />}
          {guardians.isPending && <LoadingState />}

          <Field label={t('child.reenroll.date')} required>
            {(props) => <DateInput {...props} value={date} onChange={setDate} />}
          </Field>

          {former.length > 0 ? (
            <fieldset className="dg-stack dg-stack--tight">
              <legend className="dg-field__label">{t('child.reenroll.restore')}</legend>
              {former.map((link) => (
                <label key={link.id} className="dg-checkbox">
                  <input
                    type="checkbox"
                    checked={ticked(link)}
                    onChange={(event) => toggle(link, event.target.checked)}
                  />
                  <span>
                    {t('child.reenroll.guardian', {
                      name: link.fullName ?? '—',
                      relationship: t(`guardians.relationship.${link.relationship}`),
                    })}
                    {link.accessLevel === 'Suspended' ? ` · ${t('guardians.suspended')}` : ''}
                  </span>
                </label>
              ))}
            </fieldset>
          ) : (
            guardians.data && <Notice tone="info">{t('child.reenroll.noFormer')}</Notice>
          )}

          <Row>
            <Button type="submit" busy={reenroll.isPending} busyLabel={t('action.saving')}>
              {t('child.reenroll.confirm')}
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

function latestPerGuardian(links: GuardianLinkResponse[]): GuardianLinkResponse[] {
  const latest = new Map<number, GuardianLinkResponse>();
  for (const link of links) {
    const held = latest.get(link.guardianMembershipId);
    if (!held || (link.endedAt ?? '') > (held.endedAt ?? '')) latest.set(link.guardianMembershipId, link);
  }
  return [...latest.values()];
}
